import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSettings } from '../context/SettingsContext/useSettings';
import { FindResultFilter, FindResultsState } from './types';

const SEARCH_PARAM = 'q';
const PAGE_PARAM = 'p';
const GALLERY_PAGE_PARAM = 'g';
const IMAGES_PARAM = 'im';
const AMCR_ID_PARAM = 'pa';

const filterParams = {
  sites: 'st',
  eras: 'er',
  findTypes: 'ft',
  materials: 'mt',
  groups: 'gr',
  tags: 'tg',
} as const;

type FilterKey = keyof typeof filterParams;
type MapFilter = FindResultFilter;
type ActiveMapFilter = MapFilter & { id: string; stored?: boolean };

const parseList = (value: string | null): string[] | undefined => {
  if (!value) {
    return undefined;
  }

  const items = value.split(',').filter(Boolean);
  return items.length ? items : undefined;
};

const parseBoolean = (value: string | null) => {
  if (value === 'true') {
    return true;
  }

  if (value === 'false') {
    return false;
  }

  return undefined;
};

const parsePage = (value: string | null) => {
  const page = Number(value);
  return Number.isInteger(page) && page > 1 ? page : 1;
};

export const resetFindResultsPages = (params: URLSearchParams) => {
  params.delete(PAGE_PARAM);
  params.delete(GALLERY_PAGE_PARAM);
};

export const writeFindFilterParams = (
  params: URLSearchParams,
  filter: FindResultFilter | null
) => {
  (Object.keys(filterParams) as FilterKey[]).forEach((key) => {
    params.delete(filterParams[key]);
  });
  params.delete(IMAGES_PARAM);
  params.delete(AMCR_ID_PARAM);

  if (!filter) {
    return;
  }

  (Object.keys(filterParams) as FilterKey[]).forEach((key) => {
    const values = filter[key];
    if (values?.length) {
      params.set(filterParams[key], values.join(','));
    }
  });

  if (filter.hasImages != null) {
    params.set(IMAGES_PARAM, String(filter.hasImages));
  }

  if (filter.hasAmcrId != null) {
    params.set(AMCR_ID_PARAM, String(filter.hasAmcrId));
  }
};

export const buildFindFilterSearch = (filter: FindResultFilter | null) => {
  const params = new URLSearchParams();
  writeFindFilterParams(params, filter);
  const query = params.toString();

  return query ? `?${query}` : '';
};

const getMapFilter = (
  filter: FindResultFilter | null
): MapFilter | null => {
  if (!filter) {
    return null;
  }

  const mapFilter: MapFilter = {};
  (Object.keys(filterParams) as FilterKey[]).forEach((key) => {
    if (filter[key]?.length) {
      mapFilter[key] = filter[key];
    }
  });

  if (filter.hasImages != null) {
    mapFilter.hasImages = filter.hasImages;
  }

  if (filter.hasAmcrId != null) {
    mapFilter.hasAmcrId = filter.hasAmcrId;
  }

  return Object.keys(mapFilter).length ? mapFilter : null;
};

const filtersMatch = (
  first: MapFilter | null,
  second: MapFilter | null
) => JSON.stringify(first) === JSON.stringify(second);

const mergeMapFilterIdentity = (
  activeFindFilter: ActiveMapFilter,
  nextFilter: MapFilter
): ActiveMapFilter => ({
  id: activeFindFilter.id,
  stored: activeFindFilter.stored,
  ...nextFilter,
});

export function useFindResultsState({
  syncWithMap = false,
}: {
  syncWithMap?: boolean;
} = {}): FindResultsState {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    store: { activeFindFilter },
    addFindsFilter,
    updateFindsFilter,
    disableFindsFilter,
  } = useSettings();

  const search = searchParams.get(SEARCH_PARAM) || '';
  const page = parsePage(searchParams.get(PAGE_PARAM));
  const galleryPages = parsePage(searchParams.get(GALLERY_PAGE_PARAM));

  const filter = useMemo((): FindResultFilter | null => {
    const next: FindResultFilter = {};
    let hasAny = false;

    (Object.keys(filterParams) as FilterKey[]).forEach((key) => {
      const values = parseList(searchParams.get(filterParams[key]));
      if (values) {
        next[key] = values;
        hasAny = true;
      }
    });

    const hasImages = parseBoolean(searchParams.get(IMAGES_PARAM));
    if (hasImages != null) {
      next.hasImages = hasImages;
      hasAny = true;
    }

    const hasAmcrId = parseBoolean(searchParams.get(AMCR_ID_PARAM));
    if (hasAmcrId != null) {
      next.hasAmcrId = hasAmcrId;
      hasAny = true;
    }

    return hasAny ? next : null;
  }, [searchParams]);

  const hasFilterParams = useMemo(
    () =>
      (Object.values(filterParams) as string[]).some((param) =>
        searchParams.has(param)
      ) ||
      searchParams.has(IMAGES_PARAM) ||
      searchParams.has(AMCR_ID_PARAM),
    [searchParams]
  );

  useEffect(() => {
    if (!syncWithMap) {
      return;
    }

    const activeMapFilter = getMapFilter(activeFindFilter);
    const urlMapFilter = getMapFilter(filter);

    if (!hasFilterParams && activeMapFilter) {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          writeFindFilterParams(next, activeMapFilter);
          return next;
        },
        { replace: true }
      );
      return;
    }

    if (filtersMatch(activeMapFilter, urlMapFilter)) {
      return;
    }

    if (!urlMapFilter) {
      disableFindsFilter();
    } else if (activeFindFilter) {
      updateFindsFilter(
        mergeMapFilterIdentity(activeFindFilter, urlMapFilter)
      );
    } else {
      addFindsFilter(urlMapFilter);
    }
  }, [
    activeFindFilter,
    addFindsFilter,
    disableFindsFilter,
    filter,
    hasFilterParams,
    setSearchParams,
    syncWithMap,
    updateFindsFilter,
  ]);

  const activeCount = useMemo(() => {
    if (!filter) {
      return 0;
    }

    const listCount = (Object.keys(filterParams) as FilterKey[]).filter(
      (key) => !!filter[key]?.length
    ).length;

    return (
      listCount +
      (filter.hasImages == null ? 0 : 1) +
      (filter.hasAmcrId == null ? 0 : 1)
    );
  }, [filter]);

  const setSearch = useCallback(
    (value: string | ((previous: string) => string)) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          const current = next.get(SEARCH_PARAM) || '';
          const resolved =
            typeof value === 'function' ? value(current) : value;

          if (resolved) {
            next.set(SEARCH_PARAM, resolved);
          } else {
            next.delete(SEARCH_PARAM);
          }
          resetFindResultsPages(next);

          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setFilter = useCallback(
    (nextFilter: FindResultFilter | null) => {
      if (syncWithMap) {
        const nextMapFilter = getMapFilter(nextFilter);
        if (!nextMapFilter) {
          disableFindsFilter();
        } else if (activeFindFilter) {
          updateFindsFilter(
            mergeMapFilterIdentity(activeFindFilter, nextMapFilter)
          );
        } else {
          addFindsFilter(nextMapFilter);
        }
      }

      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          writeFindFilterParams(next, nextFilter);
          resetFindResultsPages(next);

          return next;
        },
        { replace: true }
      );
    },
    [
      activeFindFilter,
      addFindsFilter,
      disableFindsFilter,
      setSearchParams,
      syncWithMap,
      updateFindsFilter,
    ]
  );

  const clearFilter = useCallback(() => {
    setFilter(null);
  }, [setFilter]);

  const setPageParam = useCallback(
    (param: string, value: number) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          if (value > 1) {
            next.set(param, String(value));
          } else {
            next.delete(param);
          }

          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setPage = useCallback(
    (value: number) => setPageParam(PAGE_PARAM, value),
    [setPageParam]
  );

  const setGalleryPages = useCallback(
    (value: number) => setPageParam(GALLERY_PAGE_PARAM, value),
    [setPageParam]
  );

  return {
    search,
    setSearch,
    filter,
    activeCount,
    setFilter,
    clearFilter,
    page,
    setPage,
    galleryPages,
    setGalleryPages,
  };
}
